/* eslint-disable max-lines */
import React, { Component, createRef } from 'react';
import { uniq } from 'lodash';
import cx from 'classnames';
import { Manager, Popper, Reference } from 'react-popper';
import { ClickOutside, FocusTrap, Portal } from 'components/Utilities';
import { deriveStateFromWatchedProps, omitProps, setRefs } from 'utils/react';
import { generateId } from 'utils/string';
import { createToggleable, ToggleableContext } from 'containers/Toggleable';
import { TransitionCollapse } from 'components/Transition';
import { ALIGNMENT, defaultProps, propTypes } from './propTypes';
import { DropdownToggleWrapper } from './components';
import styles from './Dropdown.module.scss';

const POPPER_PADDING = 15;
const HANDLE_SCROLL_DELAY = 500;

export class DropdownComponent extends Component {
    static displayName = 'Dropdown';

    static ALIGNMENT = ALIGNMENT;

    static propTypes = {
        ...propTypes,
    };

    static contextType = ToggleableContext;

    static defaultProps = {
        ...defaultProps,
    };

    static getDerivedStateFromProps(props, state) {
        return deriveStateFromWatchedProps(props, state, {
            id: () => props.id || generateId('dropdown'),
        });
    }

    state = {
        id: null,
    };

    contentRef = createRef();

    toggleRef = createRef();

    popoverRef = createRef();

    arrowRef = createRef();

    timeoutRef = createRef();

    scheduleUpdateRef = createRef();

    previousContext;

    componentDidUpdate(previousProps, previousState) {
        const { isOpen, openOnFocus } = this.props;
        const { isOpen: previousIsOpen } = previousProps;
        const { isOpen: contextIsOpen } = this.context;
        const { isOpen: previousContextIsOpen } = this.previousContext;

        if (isOpen !== previousIsOpen || contextIsOpen !== previousContextIsOpen) {
            this.updateScrollListener();

            if (this.scheduleUpdateRef.current) {
                this.scheduleUpdateRef.current();
            }
        }

        if (previousContextIsOpen === true && contextIsOpen === false && openOnFocus === false) {
            this.toggleRef.current?.focus();
        }

        this.previousContext = this.context;
    }

    componentDidMount() {
        this.previousContext = this.context;
        this.updateScrollListener();
    }

    componentWillUnmount() {
        this.removeScrollListener();
    }

    removeScrollListener() {
        if (this.timeoutRef.current) {
            clearTimeout(this.timeoutRef.current);
            window.removeEventListener('scroll', this.handleScroll);
        }
    }

    updateScrollListener() {
        const { isOpen } = this.context;

        this.removeScrollListener();

        if (isOpen) {
            this.timeoutRef.current = setTimeout(() => {
                window.addEventListener('scroll', this.handleScroll);
            }, HANDLE_SCROLL_DELAY);
        }
    }

    getPlacement(preferPositionBottomOrLeft = true, preferAlignLeft = false) {
        const { alignment, positionHorizontally } = this.props;

        if (positionHorizontally) {
            return preferPositionBottomOrLeft ? 'left' : 'right';
        }

        const preferAlignCenter = [ALIGNMENT.CENTER, ALIGNMENT.TOGGLE].includes(alignment);
        const verticalPosition = preferPositionBottomOrLeft ? 'bottom' : 'top';

        if (preferAlignCenter) return verticalPosition;

        const horizontalAlign = preferAlignLeft ? 'start' : 'end';
        return `${verticalPosition}-${horizontalAlign}`;
    }

    getPopperClassName(placedAtEnd) {
        const { arrow, positionHorizontally, disabled, usePortal, footer, dropdownClassName } =
            this.props;
        const { isOpen } = this.context;

        return cx(
            'dropdown',
            'g-dropdown',
            styles.popper,
            usePortal && styles.portal,
            arrow && styles.hasArrow,
            positionHorizontally && styles.horizontal,
            !!placedAtEnd && styles.end,
            footer && styles.hasFooter,
            {
                show: isOpen && !disabled,
                disabled,
            },
            dropdownClassName,
        );
    }

    handleScroll = () => {
        const { closeOnScroll } = this.props;
        const { isOpen, close } = this.context;

        if (closeOnScroll && isOpen) {
            close();
        }
    };

    handleClickOutside = (event, isClick) => {
        const { closeOnClickOutside, onClickOutside } = this.props;
        const { target } = event;
        const { isOpen, close } = this.context;

        if (isClick) {
            if (this.popoverRef.current?.contains(target)) {
                return;
            }

            const { current: toggle } = this.toggleRef || {};
            if (toggle === target || toggle.contains(target)) {
                return; // we don't want to calculate clicks on Toggle as clickOutside
            }
        }

        if (closeOnClickOutside && isOpen) {
            close();
        }

        onClickOutside(event, isClick);
    };

    modifyStyles = ({ state }) => {
        const { alignment, arrow, minWidth } = this.props;

        const {
            styles: { popper: popperStyles, arrow: arrowStyles },
            rects: { popper, reference },
            placement,
        } = state;

        const isSideAligned = !['top', 'bottom'].includes(placement);
        const isToggleSmaller = reference.width <= popper.width;

        if (alignment === ALIGNMENT.TOGGLE) {
            popperStyles.width = reference.width;
            popperStyles.minWidth = Math.max(reference.width, minWidth);
        }

        if (arrow && arrowStyles && isSideAligned && !isToggleSmaller) {
            arrowStyles.left = null;
            arrowStyles.transform = null;
        }
    };

    renderActionButton() {
        const { actionButton } = this.props;

        if (actionButton) {
            return <div className={styles.action}>{actionButton}</div>;
        }

        return null;
    }

    renderFooter() {
        const { footer } = this.props;

        if (!footer) return null;
        return <div className={styles.footer}>{footer}</div>;
    }

    renderContent = ({ ref, style, placement, arrowProps, update }) => {
        const {
            role,
            arrow,
            alignment,
            positionHorizontally,
            padded,
            children,
            quick,
            maxHeight,
            scrollable,
            useFocusTrap,
            containerClassName,
            contentClassName,
        } = this.props;
        const { isOpen, onOpen, onClose } = this.context;
        const { id } = this.state;

        this.scheduleUpdateRef.current = update;
        const placedAtEnd = placement?.includes('bottom') || placement?.includes('right');

        const bodyClassNames = cx(styles.body);
        const dropdownClassNames = cx(
            styles.dropdown,
            alignment === ALIGNMENT.TOGGLE && styles.useToggleWidth,
            containerClassName,
        );
        const contentClassNames = cx(styles.content, padded && styles.padded, contentClassName);
        const contentStyle = scrollable ? { maxHeight: maxHeight || null } : {};

        const content = scrollable ? (
            <div className={contentClassNames} style={contentStyle}>
                {children}
            </div>
        ) : (
            children
        );

        return (
            <FocusTrap
                active={isOpen && useFocusTrap}
                innerRef={ref}
                id={`${id}-popover`}
                role={role}
                style={style}
                data-placement={placement}
                className={dropdownClassNames}
            >
                <TransitionCollapse
                    nodeRef={this.contentRef}
                    show={!!isOpen}
                    quick={quick}
                    bottom={!placedAtEnd} // transition should go from the opposite direction
                    horizontal={positionHorizontally}
                    onEntered={onOpen}
                    onExited={onClose}
                >
                    <ClickOutside
                        innerRef={this.contentRef}
                        active={isOpen}
                        className={this.getPopperClassName(placedAtEnd)}
                        onTrigger={this.handleClickOutside}
                    >
                        <div className={bodyClassNames}>
                            {content}
                            {this.renderActionButton()}
                        </div>
                        {this.renderFooter()}
                        {arrow && (
                            <div
                                {...arrowProps}
                                ref={setRefs(arrowProps.ref, this.arrowRef)}
                                aria-hidden
                                className={styles.arrow}
                                data-placement={placement}
                            />
                        )}
                    </ClickOutside>
                </TransitionCollapse>
            </FocusTrap>
        );
    };

    renderToggle = ({ ref }) => {
        const {
            tabIndex,
            disabled,
            className,
            toggleClassName,
            renderToggle,
            toggle,
            openOnFocus,
            openOnKeyDown,
            toggleOnClick,
            toggleWithSpace,
            toggleWithEnter,
            closeWithTab,
            toggleRef,
            onKeyDown,
            onClick,
            onFocus,
            ...rest
        } = this.props;
        const { id } = this.state;

        const classNames = cx(toggleClassName, className);

        return (
            <DropdownToggleWrapper
                id={id}
                disabled={disabled}
                renderToggle={renderToggle}
                toggle={toggle}
                openOnFocus={openOnFocus}
                openOnKeyDown={openOnKeyDown}
                toggleOnClick={toggleOnClick}
                toggleWithSpace={toggleWithSpace}
                toggleWithEnter={toggleWithEnter}
                tabIndex={tabIndex}
                closeWithTab={closeWithTab}
                toggleRef={setRefs(ref, this.toggleRef, toggleRef)}
                className={classNames}
                onClick={onClick}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                {...omitProps(rest, DropdownComponent)}
            />
        );
    };

    renderPopover() {
        const { alignment, arrow, usePortal, popoverRef } = this.props;
        const { isOpen } = this.context;

        const useToggleWidth = alignment === ALIGNMENT.TOGGLE;
        const preferAlignLeft = alignment !== ALIGNMENT.RIGHT;
        const preferPositionBottomOrLeft = true;
        const placement = this.getPlacement(preferPositionBottomOrLeft, preferAlignLeft);

        const modifiers = [
            {
                name: 'flip',
                options: {
                    fallbackPlacements: uniq([
                        placement,
                        this.getPlacement(preferPositionBottomOrLeft, !preferAlignLeft),
                        this.getPlacement(!preferPositionBottomOrLeft, preferAlignLeft),
                        this.getPlacement(!preferPositionBottomOrLeft, !preferAlignLeft),
                    ]),
                    flipVariations: true,
                    rootBoundary: 'viewport',
                    padding: POPPER_PADDING,
                },
            },
            {
                name: 'preventOverflow',
                enabled: !useToggleWidth,
                options: {
                    mainAxis: true,
                    altAxis: true,
                    tether: true,
                    rootBoundary: 'document',
                    altBoundary: !usePortal,
                    padding: POPPER_PADDING,
                },
            },
            {
                name: 'hide',
                enabled: true,
            },
            {
                name: 'eventListeners',
                options: {
                    scroll: isOpen,
                    resize: isOpen,
                },
            },
            {
                name: 'computeStyles',
                options: {
                    adaptive: false,
                    gpuAcceleration: true,
                },
            },
            {
                name: 'modifyStyles',
                enabled: arrow || useToggleWidth,
                phase: 'beforeWrite',
                requires: ['computeStyles'],
                fn: this.modifyStyles,
                effect: ({ state }) => {
                    if (useToggleWidth) {
                        const { popper, reference } = state.elements;
                        popper.style.width = `${reference.offsetWidth}px`;
                    }
                },
            },
        ];

        return (
            <Portal active={usePortal}>
                <Popper
                    innerRef={setRefs(this.popoverRef, popoverRef)}
                    placement={placement}
                    modifiers={modifiers}
                >
                    {this.renderContent}
                </Popper>
            </Portal>
        );
    }

    render() {
        const { disabled, useToggleContainer, className } = this.props;

        const dropdown = (
            <Manager tag={false}>
                <Reference>{this.renderToggle}</Reference>
                {!disabled && this.renderPopover()}
            </Manager>
        );

        if (!useToggleContainer) {
            return dropdown;
        }

        const classNames = cx('dropdown', 'g-dropdown', { disabled }, className);

        return <div className={classNames}>{dropdown}</div>;
    }
}

export default createToggleable(DropdownComponent);
